import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, HelpCircle, LifeBuoy, ChevronLeft, ChevronRight } from 'lucide-react';

// Help sections with searchable text and JSX content (combined where it makes sense)
const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting started',
    searchText: 'getting started sign in save watchlists portfolios filter presets custom rules price alerts navigation home screener track portfolio watchlist news movers messages resources learn support symbol stock detail charts financials US Canada markets',
    content: (
      <>
        <ul>
          <li><strong>Sign in</strong> to save your watchlists, portfolios, filter presets, custom rules, and price alerts across devices.</li>
          <li>Use the top navigation: <strong>Home</strong>, <strong>Screener</strong>, <strong>Movers</strong>, <strong>News</strong>, and <strong>Messages</strong>. Open <strong>Track</strong> for <strong>Portfolio</strong> and <strong>Watchlist</strong>, and <strong>Resources</strong> for <strong>About Us</strong>, <strong>Help</strong>, <strong>Learn</strong>, and <strong>Support</strong>.</li>
          <li>Click any symbol to open the <strong>stock detail</strong> page for charts, financials, AI overview, and news.</li>
          <li>Bidsig covers <strong>US and Canadian</strong> markets. Portfolio and watchlist data loads the latest trade date for each market automatically.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'screener',
    title: 'Screener — Buy / Sell / Hold signals',
    searchText: 'screener dashboard buy sell hold signals trade date picker strategy momentum mean reversion market US Canada index chart pattern filters advanced filters default custom rules P/E RSI volume sector last signal apply filters save preset add rules sort column header bulk add refresh stocks ETF funds up days SMA 52W high news sentiment',
    content: (
      <>
        <p>The screener shows stocks that match built-in or your custom rules, grouped into Buy, Sell, and Hold.</p>
        <ul>
          <li><strong>Instrument tabs</strong> — Filter by <em>All</em>, <em>Stocks</em>, <em>ETF</em>, or <em>Funds</em> at the top of the page.</li>
          <li><strong>Market</strong> — Choose <em>United States</em>, <em>Canada</em>, or <em>All Markets</em>. If the selected date has no data for that market, a notice offers to jump to the latest available date.</li>
          <li><strong>Trade Date</strong> — Pick a trading date to load that day&rsquo;s signals. Use the date picker to explore past or current dates.</li>
          <li><strong>Strategy</strong> — Switch between <em>Momentum</em> and <em>Mean Reversion</em> to change which built-in rules drive the signals.</li>
          <li><strong>Index &amp; Chart Pattern</strong> — Narrow results to a market index (e.g. S&amp;P 500, TSX) or a chart pattern such as Cup &amp; Handle, Double Top, or Double Bottom.</li>
          <li><strong>Default vs Custom</strong> — Use <em>Default</em> for standard Bidsig rules, or switch to <em>Custom</em> to use your own rules (managed in <Link to="/profile">Profile → Custom Rules</Link>).</li>
          <li><strong>Filters (Advanced Filters)</strong> — Open the filter dialog to set min/max for numbers (e.g. P/E Ratio, RSI (14), Volume, Market Cap ($M), % Change (1D)) or choose options (Sector, Last Signal). Click <em>Apply Filters</em> to update the lists. When signed in, <em>Save</em> stores your preset to your account; <em>Default</em> loads your saved preset. Hover the help icon beside each field for details on Up Days, % From SMA 20, % From 52W High, Strategy Return (%), News Sentiment, and more.</li>
          <li><strong>Add Rules</strong> — Opens the custom rules editor when in Custom mode. Define buy and sell conditions using fields like <code>rsi</code>, <code>close</code>, <code>volume</code>, and operators (<code>&gt;</code>, <code>&lt;</code>, <code>BETWEEN</code>, <code>AND</code>, <code>OR</code>).</li>
          <li><strong>Column sort</strong> — Click any column header to sort. Each click cycles ascending → descending → neutral (back to symbol A–Z).</li>
          <li><strong>Bulk add</strong> — Select rows with the checkboxes, then click <em>Add</em> to add symbols to a portfolio or watchlist in one step.</li>
          <li><strong>Refresh &amp; reset</strong> — The refresh button reloads screener data. The undo button clears applied filter criteria and search while keeping date, strategy, index, patterns, market, and instrument tab settings.</li>
          <li><strong>Help icon</strong> (question mark) — Click for a short in-page guide to the screener and column meanings. Hover over the help icon next to each filter field in the filter dialog for field-specific help.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'watchlist',
    title: 'Watchlist',
    searchText: 'watchlist add new create tabs symbols watchlist summary performance news indicators sentiment symbol stock detail US Canada multi-market',
    content: (
      <>
        <p>Build and manage one or more watchlists to track stocks you&rsquo;re interested in. US and Canadian symbols are supported and load using each market&rsquo;s latest trade date.</p>
        <ul>
          <li><strong>Add New</strong> — Create a new watchlist and give it a name. You can have multiple lists (e.g. &ldquo;Tech&rdquo;, &ldquo;Dividends&rdquo;).</li>
          <li>Use the <strong>tabs</strong> to switch between watchlists. Add symbols with the symbol input; remove with the remove control.</li>
          <li><strong>Watchlist Summary</strong> — Shows aggregate performance (e.g. average daily, 1‑week, 2‑week, 4‑week) so you can see how the group is doing.</li>
          <li><strong>News indicators</strong> — Colour cues and ratings show recent news sentiment for each symbol. Click a symbol to open its detail page and read the news.</li>
          <li>Click a <strong>symbol</strong> to open the stock detail page for charts, summary, key metrics, financials, AI overview, and latest news.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'portfolio',
    title: 'Portfolio',
    searchText: 'portfolio add new create holdings buy price buy date position remove performance portfolio summary gain news indicators US Canada multi-market',
    content: (
      <>
        <p>Track your actual holdings with buy price and date to monitor performance. Supports US and Canadian symbols across multiple portfolios.</p>
        <ul>
          <li><strong>Add New</strong> — Create a portfolio (e.g. &ldquo;Taxable&rdquo;, &ldquo;IRA&rdquo;). You can maintain multiple portfolios.</li>
          <li><strong>Add a position</strong> — Enter symbol, buy price, and buy date, then add. This is used for performance and return calculations.</li>
          <li><strong>Remove</strong> — Use the remove control when you close a position.</li>
          <li><strong>Portfolio Summary</strong> — Shows aggregate performance (e.g. average daily, 1‑week, 4‑week, and average % gain) for the selected portfolio.</li>
          <li>As on the watchlist, <strong>news indicators</strong> and symbol links help you stay on top of material news for your holdings.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'stock-detail',
    title: 'Stock detail page',
    searchText: 'stock detail symbol price chart TradingView summary key metrics financials income statement balance sheet cash flow news AI overview sentiment',
    content: (
      <>
        <p>Click any symbol to open its detail page. You get:</p>
        <ul>
          <li><strong>Price and chart</strong> — Current price, change, and an interactive TradingView chart.</li>
          <li><strong>Summary</strong> — Open, previous close, day&rsquo;s change, week&rsquo;s and month&rsquo;s change, PE, EPS, volume, market cap, and related stats.</li>
          <li><strong>Key Metrics</strong> — Profitability, liquidity, leverage/valuation, and efficiency ratios.</li>
          <li><strong>AI Overview</strong> — Signed-in members get an AI-generated company summary with sentiment, key drivers, and related context. Create a free account to unlock this tab.</li>
          <li><strong>Financials</strong> — Income statement, balance sheet, and cash flow (full year and quarterly). Switch tabs and periods as needed.</li>
          <li><strong>Latest News</strong> — Recent headlines with AI sentiment ratings, source, and date; click through to read full articles.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'news-movers',
    title: 'News & Movers',
    searchText: 'news movers date search headline symbol sentiment rating content AI summary pagination gainers losers most active refresh percent change date picker two column grid',
    content: (
      <>
        <h4>News</h4>
        <p>The News page shows material press releases with AI sentiment analysis and summaries.</p>
        <ul>
          <li><strong>Date</strong> — Pick the date to view news from that day.</li>
          <li><strong>Search</strong> — Type in the search box and choose where to search: Default (all fields), Headline, Symbol, Rating, Sentiment, or Content. Results update as you type.</li>
          <li><strong>Sentiment</strong> — Each story shows an AI rating (0–10) with expandable key drivers, positive/negative factors, and related details.</li>
          <li><strong>Summary strip</strong> — A count at the top shows how many releases match your current date and search.</li>
          <li><strong>Pagination</strong> — Browse 10 stories per page. Today&rsquo;s news may refresh automatically.</li>
        </ul>
        <h4>Movers</h4>
        <p>See the day&rsquo;s biggest market moves in three tabs: <strong>Top Gainers</strong>, <strong>Top Losers</strong>, and <strong>Most Active</strong>. Use the refresh button to reload data. Click a symbol to open its stock detail page.</p>
      </>
    ),
  },
  {
    id: 'messages',
    title: 'Messages (notifications)',
    searchText: 'messages notifications alerts portfolio watchlist keywords message keywords profile headline content sentiment symbol stock alerts search refresh delete dismiss summary keyword strategy word boundary false positive earnings guidance bell dropdown',
    content: (
      <>
        <p>Messages show alerts that match your portfolio and watchlist symbols, plus any <strong>message keywords</strong> you&rsquo;ve set. The notification bell in the header shows recent <strong>news and stock alerts</strong> together.</p>
        <ul>
          <li><strong>Stock alerts</strong> — Price and signal alerts for your portfolio and watchlist symbols (Buy, Sell, Hold badges when applicable).</li>
          <li><strong>News alerts</strong> — Keyword-based notifications when headlines or content match terms you configure in <Link to="/profile">Profile → Message Keywords</Link>.</li>
          <li><strong>Summary strip</strong> — Shows total alerts plus separate counts for news and stock alerts.</li>
          <li><strong>Search</strong> — Filter alerts by symbol, message text, or keyword. Use refresh to load the latest.</li>
          <li><strong>Live vs hourly</strong> — Toggle <em>Live</em> on the Messages page to check every 30 seconds during active trading; use <em>Hourly</em> for a lighter refresh.</li>
          <li><strong>Expand &amp; delete</strong> — Click <em>More</em> on a card to see sentiment details and metadata. Use <em>Delete</em> to dismiss an alert you no longer need.</li>
        </ul>
        <p><strong>Keyword strategy (fewer false alerts)</strong></p>
        <ul>
          <li><strong>Start small</strong> — Begin with 3–5 <strong>headline</strong> keywords (e.g. <code>earnings</code>, <code>guidance</code>, <code>FDA approval</code>). Add <strong>content</strong> keywords only if you miss stories you care about.</li>
          <li><strong>Use specific phrases</strong> — Prefer multi-word terms like <code>price target</code> or <code>share repurchase</code> over single common words.</li>
          <li><strong>Avoid very short terms</strong> — Short keywords (e.g. <code>AI</code>, <code>IT</code>) can match unrelated words. Bidsig uses whole-word matching, but longer phrases still work best.</li>
          <li><strong>Sentiment keywords</strong> — Match AI analysis fields: <code>Buy</code>, <code>Beat</code>, <code>overvalued</code>, or driver names from news ratings.</li>
          <li><strong>Keep lists focused</strong> — Portfolio and watchlist symbols already trigger alerts; trim watchlists to names you actively track.</li>
          <li><strong>Layer price alerts</strong> — In Profile → Price Alerts, set custom % bounds so stock alerts fire only on moves you care about.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'profile',
    title: 'Profile & settings',
    searchText: 'profile settings account price alerts message keywords custom rules buy sell rules backtest account menu',
    content: (
      <>
        <p>From your profile (account menu → Account), you can:</p>
        <ul>
          <li><strong>Profile</strong> — Update account details and preferences.</li>
          <li><strong>Price Alerts</strong> — Set price alerts for individual portfolio and watchlist symbols.</li>
          <li><strong>Message Keywords</strong> — Add or remove headline, content, sentiment, and symbol keywords so Messages surfaces relevant news.</li>
          <li><strong>Custom Rules</strong> — Define buy and sell rules. Use field names (e.g. <code>rsi</code>, <code>close</code>, <code>volume</code>), operators (<code>&gt;</code>, <code>&lt;</code>, <code>BETWEEN</code>), and combine with <code>AND</code> / <code>OR</code> and parentheses. Save rules, then on the screener switch to <em>Custom</em> and use <em>Add Rules</em> to refine. Your custom lists act as a backtest: you see which stocks would have been Buy/Sell/Hold on the selected date.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'custom-rules',
    title: 'Custom rules & backtesting',
    searchText: 'custom rules backtest backtesting buy sell logic historical date profile syntax rsi close volume BETWEEN AND OR rule syntax help screener',
    content: (
      <>
        <p>Advanced users can define their own buy/sell logic and see how it would have performed on historical dates.</p>
        <ul>
          <li><strong>Where to set rules</strong> — Profile → Custom Rules, or Add Rules on the screener when in Custom mode.</li>
          <li><strong>Syntax</strong> — Conditions like <code>rsi &gt; 70</code>, <code>close &gt; sma20_prc</code>, <code>volume &gt; 1000000</code>, or <code>rsi BETWEEN 30 AND 70</code>. Combine with <code>AND</code>/<code>OR</code> and parentheses, e.g. <code>(rsi &lt; 30) AND (volume &gt; 500000)</code>. The rules modal includes a <strong>Rule Syntax Help</strong> section with more examples.</li>
          <li><strong>Backtest</strong> — On the screener, choose <em>Custom</em>, pick a past date, and apply filters if you want. The Buy/Sell/Hold lists show which stocks would have matched your rules on that date. Change the date to see how your strategy would have performed over time.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'learn',
    title: 'Learn (Knowledge Center)',
    searchText: 'learn knowledge center articles glossary beginner intermediate advanced investing education research tags',
    content: (
      <>
        <p>The <Link to="/learn">Learn</Link> page is Bidsig&rsquo;s Knowledge Center — educational articles on investing concepts, platform features, and market analysis.</p>
        <ul>
          <li><strong>Browse by level</strong> — Filter articles by Beginner, Intermediate, or Advanced.</li>
          <li><strong>Search</strong> — Find topics by title, summary, tags, or glossary terms.</li>
          <li><strong>Articles</strong> — Each article includes key points, sections, a glossary, and further reading links.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'newsletter',
    title: 'Newsletter',
    searchText: 'newsletter subscribe email market insights product updates research double opt-in confirm unsubscribe',
    content: (
      <>
        <p>Subscribe to the Bidsig newsletter for curated market insights, research highlights, and product updates.</p>
        <ul>
          <li><strong>Subscribe</strong> — Visit the <Link to="/newsletter">Newsletter</Link> page or use the signup banner on Home, Learn, News, About, and Support pages.</li>
          <li><strong>Confirm your email</strong> — After subscribing, check your inbox and click the confirmation link to complete double opt-in.</li>
          <li><strong>Unsubscribe anytime</strong> — Every email includes an unsubscribe link. No spam.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'tips',
    title: 'Tips for the best experience',
    searchText: 'tips log in filters save preset screener help watchlist combine stay informed keywords alerts learn newsletter',
    content: (
      <ul>
        <li><strong>Log in</strong> — Saving presets, watchlists, portfolios, rules, and alerts requires an account. Your data syncs across sessions.</li>
        <li><strong>Use filters</strong> — Narrow the screener by Sector, P/E Ratio, RSI, Volume, Market Cap ($M), or other Advanced Filters fields so signals match your style. Save your preferred filters as the default (when signed in).</li>
        <li><strong>Use the screener help icon</strong> — Click the question mark on the screener for a quick reference to dates, filters, sort, and column definitions.</li>
        <li><strong>Combine watchlist + screener</strong> — Add candidates to a watchlist, then check the screener for signals and the stock detail page for financials, AI overview, and news before deciding.</li>
        <li><strong>Stay informed</strong> — Set 3–5 specific headline keywords and price alerts in Profile. Keep watchlists focused so Messages and the notification bell stay actionable.</li>
        <li><strong>Keep learning</strong> — Visit <Link to="/learn">Learn</Link> for guides on investing concepts, and subscribe to the <Link to="/newsletter">Newsletter</Link> for market insights and product updates.</li>
      </ul>
    ),
  },
  {
    id: 'contact',
    title: 'Need more help?',
    searchText: 'support contact help email phone live chat sign in member',
    content: (
      <>
        <p>Visit <Link to="/support">Support</Link> for contact options:</p>
        <ul>
          <li><strong>Help Center</strong> — You&rsquo;re here! Search articles above for step-by-step guides.</li>
          <li><strong>Contact form</strong> — Signed-in members can send a message directly from the Support page. Sign in to use the form; we reply to your account email.</li>
        </ul>
      </>
    ),
  },
];

const Help = () => {
  const [query, setQuery] = useState('');
  const [activeSectionId, setActiveSectionId] = useState(HELP_SECTIONS[0].id);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_SECTIONS;
    return HELP_SECTIONS.filter(
      (section) =>
        section.title.toLowerCase().includes(q) ||
        section.searchText.toLowerCase().includes(q)
    );
  }, [query]);

  // When search changes, if current section isn't in filtered list, select first match
  useEffect(() => {
    const ids = filteredSections.map((s) => s.id);
    if (!ids.includes(activeSectionId) && ids.length > 0) {
      setActiveSectionId(ids[0]);
    }
  }, [filteredSections, activeSectionId]);

  const hasQuery = query.trim().length > 0;
  const noResults = hasQuery && filteredSections.length === 0;
  const activeSection = useMemo(
    () => HELP_SECTIONS.find((s) => s.id === activeSectionId),
    [activeSectionId]
  );

  return (
    <div className="page-content info-page help-page">
      <header className="info-page-header info-page-header--actions">
        <h2>
          <HelpCircle size={22} className="info-page-header-icon" aria-hidden />
          Help Center
        </h2>
        <div className="info-page-header-actions">
          <Link to="/support" className="pw-btn pw-btn-primary">
            <LifeBuoy size={16} aria-hidden />
            Contact Support
          </Link>
        </div>
      </header>

      <div className="help-search-bar">
        <label htmlFor="help-search" className="help-search-label">
          Search help articles
        </label>
        <div className="help-search-wrap pw-search-wrap help-search-wrap--wide">
          <Search className="pw-search-icon" size={16} aria-hidden />
          <input
            id="help-search"
            type="search"
            className="pw-search-input help-search-input--wide"
            placeholder="e.g. screener, filter, watchlist, backtest, RSI, newsletter..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            aria-describedby={hasQuery ? 'help-search-results' : undefined}
          />
          {query && (
            <button
              type="button"
              className="pw-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {hasQuery && (
          <p id="help-search-results" className="help-search-results" aria-live="polite">
            {noResults
              ? 'No matching articles. Try different keywords (e.g. screener, portfolio, rules, learn).'
              : `${filteredSections.length} ${filteredSections.length === 1 ? 'article' : 'articles'} found`}
          </p>
        )}
      </div>

      {noResults ? (
        <div className="help-no-results">
          <p>We couldn't find any help articles matching "<strong>{query.trim()}</strong>".</p>
          <p>Try searching for: <strong>screener</strong>, <strong>watchlist</strong>, <strong>portfolio</strong>, <strong>filter</strong>, <strong>custom rules</strong>, <strong>backtest</strong>, <strong>news</strong>, <strong>learn</strong>, or <strong>messages</strong>.</p>
        </div>
      ) : (
        <div className={`help-layout kc-layout ${sidebarOpen ? '' : 'kc-layout--sidebar-collapsed'}`}>
          <nav
            className={`help-sidebar kc-sidebar ${sidebarOpen ? '' : 'kc-sidebar--collapsed'}`}
            aria-label="Help topics"
          >
            <div className="kc-sidebar-inner">
              <div className="kc-sidebar-header">
                {sidebarOpen ? (
                  <p className="kc-sidebar-heading">Articles</p>
                ) : (
                  <span className="kc-sidebar-label-vertical" aria-hidden>Articles</span>
                )}
                <button
                  type="button"
                  className="kc-sidebar-toggle"
                  onClick={() => setSidebarOpen((open) => !open)}
                  aria-expanded={sidebarOpen}
                  aria-label={sidebarOpen ? 'Collapse articles menu' : 'Expand articles menu'}
                >
                  {sidebarOpen ? <ChevronLeft size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}
                </button>
              </div>
              {sidebarOpen && (
                <ul className="help-nav-list">
                  {filteredSections.map((section) => (
                    <li key={section.id} className="help-nav-list-item">
                      <button
                        type="button"
                        className={`help-nav-item ${activeSectionId === section.id ? 'active' : ''}`}
                        onClick={() => setActiveSectionId(section.id)}
                        aria-current={activeSectionId === section.id ? 'true' : undefined}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </nav>
          <main className="help-content info-data-card" id="help-article">
            {activeSection && (
              <section
                className={`help-section ${activeSection.id === 'tips' ? 'help-tips' : ''}`}
                id={activeSection.id}
                aria-labelledby="help-section-title"
              >
                <h2 id="help-section-title" className="help-section-title">{activeSection.title}</h2>
                <div className="help-section-body">
                  {activeSection.content}
                </div>
              </section>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Help;
